import { QueryFilter } from '@softline/core';
import type { GridOptions, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import { GetTreePath } from '../types/grid';

/** AG SSRM tree options for a `server` tree path; nothing for every other tree or none. */
export function serverTreeOptions<T extends object>(path: GetTreePath<T> | undefined): Partial<GridOptions> {
  if (path?.type !== 'server') return {};
  return {
    isServerSideGroup: (data: T) => path.isGroup(data),
    getServerSideGroupKey: (data: T) => path.groupKey(data),
  };
}

function and<T extends object>(a: QueryFilter<T> | null, b: QueryFilter<T> | null | undefined): QueryFilter<T> | null {
  if (!b) return a;
  if (!a) return b;
  return { type: 'conditions', operator: 'AND', filters: [a, b] };
}

const TREE_COL_ID = 'ag-Grid-AutoColumn';

/** A tree child request: drop the group keys (they are not row-group columns) and filter the
 *  children by their parent instead; a top-level request gets the top-level filter. A sort on
 *  the tree column becomes a sort on `sortColId`. */
export function serverTreeRequest<T extends object>(
  path: GetTreePath<T> | undefined,
  request: IServerSideGetRowsRequest,
  filter: QueryFilter<T> | null
): { request: IServerSideGetRowsRequest; filter: QueryFilter<T> | null } {
  if (path?.type !== 'server') return { request, filter };
  const keys = request.groupKeys ?? [];
  const child = keys.length > 0;
  const tree = child ? path.childFilter(keys[keys.length - 1]) : path.topLevelFilter;
  const base = child && path.childrenIgnoreFilter ? null : filter;
  const sortColId = path.sortColId;
  const sortModel = sortColId
    ? request.sortModel?.map((s) => (s.colId === TREE_COL_ID ? { ...s, colId: sortColId } : s))
    : request.sortModel;
  return { request: { ...request, groupKeys: [], sortModel }, filter: and(base, tree) };
}

/** The tree column sorts only when a server tree names the column it sorts by. */
export function treeColumnSort<T extends object>(
  path: GetTreePath<T> | undefined
): { sortable: boolean; sort?: 'asc' | 'desc' } {
  if (path?.type !== 'server' || !path.sortColId) return { sortable: false };
  return { sortable: true, ...(path.sort ? { sort: path.sort } : {}) };
}
